/**
 * Expression escaping utilities for Power Automate flow definitions.
 *
 * The Logic Apps API fails when HTTP action `body` properties are JSON objects
 * containing @{} template expressions. The template engine tries to evaluate
 * expressions at the object level during save, which breaks.
 *
 * Fix: detect these cases and convert the object body to a string expression
 * that builds the JSON at runtime using concat/json functions.
 */

import { createChildLogger } from "./logger.js";

const logger = createChildLogger({ module: "expression-escape" });

/**
 * Check if a value contains Power Automate template expressions (@{...}).
 */
export function containsExpressions(value: unknown): boolean {
  if (typeof value === "string") {
    return value.includes("@{") || (value.startsWith("@") && !value.startsWith("@@"));
  }
  if (Array.isArray(value)) {
    return value.some(containsExpressions);
  }
  if (value && typeof value === "object") {
    return Object.values(value).some(containsExpressions);
  }
  return false;
}

/**
 * Convert a JSON object with expressions into a string expression.
 *
 * Takes an object like:
 * ```json
 * {
 *   "message": {
 *     "subject": "@{variables('foo')}",
 *     "body": { "content": "@{outputs('bar')}" }
 *   }
 * }
 * ```
 *
 * And converts it to a string expression that builds the same JSON at runtime:
 * ```
 * @{json(concat('{"message":{"subject":"', variables('foo'), '","body":{"content":"', outputs('bar'), '"}}'))}
 * ```
 *
 * This avoids the template engine trying to parse expressions inside object values.
 */
export function objectToStringExpression(obj: unknown): string {
  return buildRuntimeJsonExpression(obj, false);
}

/**
 * Same conversion, but producing a value that evaluates to an OBJECT at runtime.
 *
 * `@{json(...)}` is string INTERPOLATION — its result is a String. That is fine for an
 * HTTP body, but a connector parameter declared as an object in the connector's swagger
 * is type-checked when the flow is saved, and the save fails with:
 *
 *   Input parameter 'body' validation failed in workflow operation '<action>':
 *   ... type/format 'String' is not convertible to type/format 'Object'.
 *
 * The bare `@json(...)` form is a raw expression, so it keeps the declared object type
 * while still being a SINGLE expression — which is what avoids the template engine
 * evaluating expressions at the object level during save (the reason this module exists).
 */
export function objectToObjectExpression(obj: unknown): string {
  return buildRuntimeJsonExpression(obj, true);
}

function buildRuntimeJsonExpression(obj: unknown, asObject: boolean): string {
  // Serialize to JSON string
  const jsonStr = JSON.stringify(obj);

  // Split on @{...} expression boundaries using regex
  // Pattern: match @{ followed by balanced braces until closing }
  const segments: string[] = [];
  let pos = 0;

  // Track whether the scan cursor is currently inside a JSON string value, so an
  // expression is JSON-escaped whether it sits at the START of a value ("@{x}") or
  // in the MIDDLE ("prefix @{x} suffix"). A positional "is the char before @{ a
  // quote" check misses the mid-string case and splices raw output into the JSON.
  let inJsonString = false;
  const advanceStringState = (chunk: string): void => {
    for (let k = 0; k < chunk.length; k++) {
      const c = chunk[k];
      if (c === "\\") { k++; continue; } // JSON escape — skip the next char
      else if (c === '"') inJsonString = !inJsonString;
    }
  };

  while (pos < jsonStr.length) {
    const exprStart = jsonStr.indexOf("@{", pos);
    if (exprStart === -1) {
      segments.push("'" + escapeForConcat(jsonStr.slice(pos)) + "'");
      break;
    }

    // Literal before the expression — kept verbatim (including any JSON quotes),
    // then used to update whether we're inside a JSON string value.
    if (exprStart > pos) {
      const literal = jsonStr.slice(pos, exprStart);
      segments.push("'" + escapeForConcat(literal) + "'");
      advanceStringState(literal);
    }

    // Find matching closing brace (handle nested braces). Track single-quoted
    // WDL string-literal state so a brace inside a literal — e.g. @{concat('}')} —
    // is not mistaken for the closing brace. In WDL a literal single quote is
    // written as '' (a doubled quote).
    let depth = 0;
    let exprEnd = -1;
    let inString = false;
    for (let i = exprStart + 2; i < jsonStr.length; i++) {
      const ch = jsonStr[i];
      if (ch === "'") {
        if (inString && jsonStr[i + 1] === "'") {
          i++; // escaped quote ('') inside a string literal — skip both
          continue;
        }
        inString = !inString;
        continue;
      }
      if (inString) continue;
      if (ch === "{") depth++;
      else if (ch === "}") {
        if (depth === 0) { exprEnd = i; break; }
        depth--;
      }
    }

    if (exprEnd === -1) {
      segments.push("'" + escapeForConcat(jsonStr.slice(exprStart)) + "'");
      break;
    }

    // Extract expression content without @{ and }
    const expr = jsonStr.slice(exprStart + 2, exprEnd);

    if (inJsonString) {
      // The expression sits inside a JSON string value (start OR middle). JSON-escape
      // its runtime output so a value containing a double quote, backslash, or newline
      // still produces valid JSON that json() can parse (e.g. a subject: He said "hi").
      // The surrounding quotes are already part of the literal segments.
      segments.push(jsonEscapeExpr(expr));
    } else {
      segments.push(expr);
    }

    // Advance past expression
    pos = exprEnd + 1;
  }

  // Filter empty segments and build concat
  const filtered = segments.filter(s => s !== "''" && s !== "");
  if (filtered.length === 0) return jsonStr;
  if (filtered.length === 1 && !filtered[0]!.startsWith("'")) {
    // Single expression — no concat needed
    return asObject ? `@${filtered[0]}` : `@{${filtered[0]}}`;
  }

  return asObject
    ? `@json(concat(${filtered.join(",")}))`
    : `@{json(concat(${filtered.join(",")}))}`;
}

/**
 * Wrap a string-position expression so its RUNTIME output is JSON-string-safe.
 *
 * When an expression sits inside a JSON string value ("...@{expr}..."), its output
 * is spliced between literal double quotes. If that output contains a `"`, `\`, or a
 * newline, the surrounding JSON becomes invalid and the `json(...)` call fails the run.
 * We escape it at runtime, in JSON order: backslash first (so the escapes we add below
 * aren't doubled), then double-quote, then CR/LF.
 *
 * WDL literal notes: `'\'` is one backslash, `'\\'` is two; `'\"'` is backslash+quote;
 * `'\r'`/`'\n'` are the two-char JSON escapes; `decodeUriComponent('%0D'|'%0A')` yields
 * the actual CR/LF characters to search for (they can't be written literally in a
 * single-line WDL string).
 */
function jsonEscapeExpr(expr: string): string {
  let e = expr;
  e = `replace(${e}, '\\', '\\\\')`; // \  -> \\   (must run first)
  e = `replace(${e}, '"', '\\"')`; //   "  -> \"
  e = `replace(${e}, decodeUriComponent('%0D'), '\\r')`; // CR -> \r
  e = `replace(${e}, decodeUriComponent('%0A'), '\\n')`; // LF -> \n
  return e;
}

/**
 * Escape single quotes and @ signs for use inside concat() string literals.
 */
function escapeForConcat(s: string): string {
  // In Power Automate expressions:
  // - single quotes are escaped by doubling them
  // - @ at the start of a value is escaped as @@ to prevent expression parsing
  //   (but only @ followed by a letter — @{ is already handled as an expression)
  return s.replace(/'/g, "''");
  // Note: @ in JSON key names like "@odata.type" don't need escaping inside
  // concat string literals — they're just string data at that point.
}

function preprocessActionInputs(
  inputs: Record<string, unknown>,
  actionName: string
): Record<string, unknown> {
  const processedInputs = { ...inputs };

  if (processedInputs.body && typeof processedInputs.body === "object") {
    // Check for @ in object keys (e.g., @odata.type) — these MUST be escaped or the
    // template engine tries to parse them as expressions
    const bodyStr = JSON.stringify(processedInputs.body);
    const hasAtKeys = /"@[a-zA-Z]/.test(bodyStr);

    if (hasAtKeys || containsExpressions(processedInputs.body)) {
      logger.info({ action: actionName, field: "body", hasAtKeys }, "Converting action payload object to string expression");
      processedInputs.body = objectToStringExpression(processedInputs.body);
    }
  }

  if (processedInputs.parameters && typeof processedInputs.parameters === "object" && !Array.isArray(processedInputs.parameters)) {
    const parameters = processedInputs.parameters as Record<string, unknown>;
    let changed = false;
    const processedParameters: Record<string, unknown> = { ...parameters };

    for (const [key, value] of Object.entries(parameters)) {
      if (value && typeof value === "object" && containsExpressions(value)) {
        // Connector parameters are type-checked against the connector's swagger at save
        // time, so this must stay an object — see objectToObjectExpression.
        logger.info({ action: actionName, field: `parameters.${key}` }, "Converting connector parameter object to runtime json expression");
        processedParameters[key] = objectToObjectExpression(value);
        changed = true;
      }
    }

    if (changed) {
      processedInputs.parameters = processedParameters;
    }
  }

  return processedInputs;
}

/**
 * Process all actions in a flow definition to fix expression-in-object issues.
 *
 * Walks the action tree (including nested actions in Foreach/If/Switch/Scope)
 * and converts payload objects with expressions to runtime JSON expressions.
 */
export function preprocessFlowActions(
  actions: Record<string, unknown>
): Record<string, unknown> {
  const processed: Record<string, unknown> = {};

  for (const [name, action] of Object.entries(actions)) {
    if (!action || typeof action !== "object") {
      processed[name] = action;
      continue;
    }

    const actionObj = action as Record<string, unknown>;
    processed[name] = { ...actionObj };
    const processedAction = processed[name] as Record<string, unknown>;

    // `typeof [] === "object"`, and preprocessActionInputs spreads its argument
    // into an object literal — so an ARRAY `inputs` came back as {"0":…,"1":…}.
    // A Compose holding a literal list is the common case, and Foreach then
    // rejected it with "the result of the evaluation of 'foreach' expression is
    // of type 'Object'. The result must be a valid array."
    // An array has no `body`/`parameters` to fix, so passing it through is lossless.
    if (
      actionObj.inputs &&
      typeof actionObj.inputs === "object" &&
      !Array.isArray(actionObj.inputs)
    ) {
      processedAction.inputs = preprocessActionInputs(
        actionObj.inputs as Record<string, unknown>,
        name
      );
    }

    // Recursively process nested actions (Foreach, If, Switch, Scope, Until)
    if (actionObj.actions && typeof actionObj.actions === "object") {
      processedAction.actions = preprocessFlowActions(
        actionObj.actions as Record<string, unknown>
      );
    }
    // If/Switch else branch
    if (actionObj.else && typeof actionObj.else === "object") {
      const elseBranch = actionObj.else as Record<string, unknown>;
      if (elseBranch.actions && typeof elseBranch.actions === "object") {
        processedAction.else = {
          ...elseBranch,
          actions: preprocessFlowActions(elseBranch.actions as Record<string, unknown>),
        };
      }
    }
    // Switch cases
    if (actionObj.cases && typeof actionObj.cases === "object") {
      const cases = actionObj.cases as Record<string, unknown>;
      const processedCases: Record<string, unknown> = {};
      for (const [caseName, caseVal] of Object.entries(cases)) {
        if (caseVal && typeof caseVal === "object") {
          const cv = caseVal as Record<string, unknown>;
          if (cv.actions && typeof cv.actions === "object") {
            processedCases[caseName] = {
              ...cv,
              actions: preprocessFlowActions(cv.actions as Record<string, unknown>),
            };
          } else {
            processedCases[caseName] = caseVal;
          }
        } else {
          processedCases[caseName] = caseVal;
        }
      }
      processedAction.cases = processedCases;
    }
  }

  return processed;
}
